import React, { Component } from "react";
import { observer } from "mobx-react";
import { Link } from "react-router-dom";
import Currencies from "./Currencies";
import currencyStore from "../../stores/CurrencyStore";
import ModalStore from "../../stores/ModalStore";
import PricingStore from "../../stores/PricingStore";
import { CurrentUserResource } from "../../stores/CurrentUserStore/Types";

interface Props {
  pricingStore?: PricingStore;
  updateSelectedCurrency?: (isoCode: string) => void;
  layout: string;
  currentUser: CurrentUserResource;
}

interface FooterSkillArea {
  name: string;
  path: string;
}

class Footer extends Component<Props, {}> {
  modalStore = new ModalStore();

  componentDidMount() {
    this.modalStore.ready();
  }

  renderCurrencies = () => {
    const { layout } = this.props;

    switch (currencyStore.state.kind) {
      case "ready":
        if (layout !== "lesson") {
          return (
            <Currencies
              modalStore={this.modalStore}
              currencies={currencyStore.currencies}
              pricingStore={this.props.pricingStore}
              updateSelectedCurrency={this.props.updateSelectedCurrency}
            />
          );
        } else {
          return <div />;
        }

      default:
        return <div />;
    }
  };

  footerGreeting = () => {
    const now = new Date();
    const day = now.getDay(); // 0 = Sun, 6 = Sat
    const hour = now.getHours();

    if (day === 0) {
      return "It's Sunday. We respect your dedication. We also respect a long lunch.";
    }

    if (day === 6) {
      return "Saturday. The most under-rated day for thinking properly.";
    }

    if (day === 5 && hour >= 14 && hour <= 18) {
      return "Friday afternoon. The official browsing hour of the British workforce.";
    }

    if (day === 1 && hour >= 5 && hour <= 11) {
      return "Monday morning. We salute you for not opening social media first.";
    }

    if (hour >= 22 || hour < 5) {
      return "It's late where you are. The courses will still be here tomorrow. Probably get some sleep.";
    }

    if (hour >= 5 && hour < 9) {
      return "You're up early. The pre-coffee mind is allegedly more creative. Allegedly.";
    }

    if (hour >= 9 && hour < 12) {
      return "Mid-morning. Peak brain. Also peak procrastination. Choose wisely.";
    }

    if (hour >= 12 && hour < 14) {
      return "Lunchtime. Browsing while chewing is a recognised art form.";
    }

    if (hour >= 14 && hour < 17) {
      return "Afternoon. The hour the internet was very nearly designed for.";
    }

    return "Evening. A perfectly reasonable hour for a small intellectual nibble.";
  };
  footerSkillAreas = (): FooterSkillArea[] => {
    return ((window as any).gon?.footer_skill_areas || []) as FooterSkillArea[];
  };

  render() {
    const { layout, currentUser } = this.props;
    const currentYear = new Date().getFullYear();
    const footerSkillAreas = this.footerSkillAreas();

    return (
      <footer
        id="footer"
        className="facelift-footer site-footer"
        aria-label="Footer"
      >
        <div className="inner">
          <div className="footer-grid">
            <div className="col-brand">
              <div className="footer-logo">
                <a href="/" aria-label="42courses home">
                  <img
                    src={require("../../../../assets/images/landings/footer-logo.svg")}
                    alt="42courses logo"
                  />
                </a>
              </div>
              <p className="footer-blurb">
                Personalised, CPD-accredited Mini MBAs. Co-created with people
                who actually do the work for a living.
              </p>
              <div className="socials" aria-label="42courses on social media">
                <a
                  href="https://www.linkedin.com/company/42courses"
                  aria-label="42courses on LinkedIn"
                  rel="noopener"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5 1.78 1.78 0 016.5 8.25zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
                  </svg>
                </a>
                <a
                  href="https://x.com/42courses"
                  aria-label="42courses on X"
                  rel="noopener"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
                  </svg>
                </a>
                <a
                  href="https://www.instagram.com/42courses/"
                  aria-label="42courses on Instagram"
                  rel="noopener"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2.16c3.2 0 3.58 0 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.65.07 4.85s0 3.58-.07 4.85c-.15 3.23-1.66 4.77-4.92 4.92-1.27.06-1.65.07-4.85.07s-3.58 0-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92C2.18 15.58 2.16 15.2 2.16 12s0-3.58.07-4.85C2.38 3.92 3.9 2.38 7.15 2.23 8.42 2.18 8.8 2.16 12 2.16zm0 5.18A4.66 4.66 0 1016.66 12 4.66 4.66 0 0012 7.34zm0 7.7A3.04 3.04 0 1115.04 12 3.04 3.04 0 0112 15.04zm4.84-9a1.09 1.09 0 101.09 1.08 1.08 1.08 0 00-1.09-1.08z"></path>
                  </svg>
                </a>
                <a
                  href="https://www.youtube.com/c/42courses"
                  aria-label="42courses on YouTube"
                  rel="noopener"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M23.5 6.51a3.02 3.02 0 00-2.12-2.13C19.5 3.86 12 3.86 12 3.86s-7.5 0-9.38.52A3.02 3.02 0 00.5 6.51 31.4 31.4 0 000 12a31.4 31.4 0 00.5 5.49 3.02 3.02 0 002.12 2.13C4.5 20.14 12 20.14 12 20.14s7.5 0 9.38-.52a3.02 3.02 0 002.12-2.13A31.4 31.4 0 0024 12a31.4 31.4 0 00-.5-5.49zM9.6 15.6V8.4l6.24 3.6z"></path>
                  </svg>
                </a>
                <a
                  href="https://open.spotify.com/show/0G3ZhYVoVIKWVbbATFsm6X"
                  aria-label="42courses Podcast on Spotify"
                  rel="noopener"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.5 14.4c-.22.35-.67.46-1.02.24-2.8-1.71-6.32-2.1-10.46-1.16-.4.09-.8-.16-.89-.56s.16-.8.56-.89c4.54-1.04 8.42-.59 11.55 1.33.35.22.46.67.26 1.04zm1.2-2.94c-.27.43-.84.57-1.27.3-3.21-1.97-8.1-2.54-11.89-1.39-.5.15-1.03-.13-1.18-.63s.13-1.03.63-1.18c4.32-1.31 9.71-.68 13.4 1.59.42.26.57.84.31 1.31zm.1-3.06c-3.84-2.28-10.18-2.5-13.83-1.39-.6.18-1.23-.16-1.41-.76s.16-1.23.76-1.41c4.2-1.27 11.18-1.02 15.6 1.6.54.32.72 1.02.4 1.56s-1 .73-1.52.4z"></path>
                  </svg>
                </a>
                <a
                  href="https://podcasts.apple.com/gb/podcast/42courses-podcast/id1481001984"
                  aria-label="42courses Podcast on Apple Podcasts"
                  rel="noopener"
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="18.5" r="2"></circle>
                    <path
                      d="M7.5 16.2c-1.4-1.2-2.3-3-2.3-5 0-3.6 3-6.5 6.8-6.5s6.8 2.9 6.8 6.5c0 2-.9 3.8-2.3 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    ></path>
                    <path
                      d="M9.6 14.2c-.8-.7-1.3-1.7-1.3-2.9 0-2.1 1.7-3.7 3.7-3.7s3.7 1.7 3.7 3.7c0 1.1-.5 2.2-1.3 2.9"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    ></path>
                    <circle cx="12" cy="11.3" r="1.4"></circle>
                  </svg>
                </a>
              </div>
            </div>

            <div className="footer-col">
              <h4>Learn</h4>
              <ul>
                <li>
                  <a href="/courses">Courses</a>
                </li>
                <li>
                  <a href="/workshops">Live Workshops</a>
                </li>
                <li>
                  <a href="/community">Community Hub</a>
                </li>
                <li>
                  <a href="/team_plans">For Teams</a>
                </li>
                <li>
                  <a href="/blog">Blog</a>
                </li>
                <li>
                  <Link to="/podcasts">Podcast</Link>
                </li>
                <li>
                  <Link to="/newsletters">Newsletter archive</Link>
                </li>
                <li>
                  <Link to="/rory-quotes">Rory Quote Finder</Link>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>Company</h4>
              <ul>
                <li>
                  <Link to="/about">About Us</Link>
                </li>
                <li>
                  <Link to="/pricing">Pricing</Link>
                </li>
                <li>
                  <Link to="/faqs">FAQs</Link>
                </li>
                <li>
                  <a href="mailto:groundcontrol@42courses.com">Contact</a>
                </li>
              </ul>
            </div>

            {/* Names and links both come from the server (see
                ApplicationController#footer_skill_areas) rather than being
                written out here, which is how the labels and anchors drifted
                apart from the courses page in the first place. */}
            {footerSkillAreas.length > 0 && (
              <div className="footer-col">
                <h4>Skill Areas</h4>
                <ul>
                  {footerSkillAreas.map((skillArea) => (
                    <li key={skillArea.path}>
                      <a href={skillArea.path}>{skillArea.name}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="footer-col">
              <h4>Account</h4>
              <ul>
                {currentUser?.kind === "current-user-absent" && (
                  <li>
                    <a href="/login">Sign In</a>
                  </li>
                )}
                <li>
                  <a href="/courses#sample-courses">Try a course on us</a>
                </li>
                <li>
                  <a href="/gift">Gifting</a>
                </li>
              </ul>
              {layout !== "lesson" && (
                <button
                  type="button"
                  id="currency-button"
                  className="currency-button"
                  onClick={() => this.modalStore.open()}
                >
                  <span
                    className={`currency-flag currency-flag-${currencyStore.isoCode.toLowerCase()}`}
                    aria-hidden="true"
                  ></span>
                  <span>{currencyStore.isoCode.toUpperCase()}</span>
                  <i className="setting icon" aria-hidden="true"></i>
                </button>
              )}
            </div>
          </div>

          <p className="footer-microcopy" aria-live="polite">
            {this.footerGreeting()}
          </p>

          <div className="footer-bottom">
            <div>
              © 2015 - {currentYear} 42courses.com ltd. · Mini MBAs for Big
              Thinkers
            </div>
            <nav className="legal" aria-label="Legal links">
              <Link to="/privacy">Privacy</Link>
              <a href="/terms">Terms</a>
              <Link to="/cookies">Cookies</Link>
              <Link to="/accessibility">Accessibility</Link>
            </nav>
          </div>
          {this.renderCurrencies()}
        </div>
      </footer>
    );
  }
}

export default observer(Footer);
